import {
  authAtom,
  getPrimaryColor,
  PAGE_NAME,
  useRecoil,
} from '@orcar/common-d2c';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { app } from '@/constants/common';

type MyPageWrapperProps = {
  isMobile: boolean;
  children: React.ReactNode;
};

type MyPageTab = {
  id: number;
  label: string;
  pathname: string;
};

export const MY_PAGE_TABS: MyPageTab[] = [
  {
    id: 0,
    label: '내 예약 목록',
    pathname: '',
  },
  {
    id: 1,
    label: '마일리지',
    pathname: '/mileage',
  },
  {
    id: 2,
    label: '친구 초대',
    pathname: '/referral',
  },
];

const MyPageWrapper = ({ isMobile, children }: MyPageWrapperProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { recoilValue: user } = useRecoil(authAtom);

  const isMyPage = pathname.includes(PAGE_NAME.my_page);

  // 현재 경로 기반 활성 탭 (직접 진입/뒤로가기에도 정확). '내 예약 목록'은 pathname ''.
  const activeTabId =
    MY_PAGE_TABS.find((tab) => tab.pathname && pathname.includes(tab.pathname))
      ?.id ?? MY_PAGE_TABS[0].id;

  const handleTabClick = (tabPathname: string) => {
    navigate(PAGE_NAME.my_page + tabPathname);
  };

  // 모바일: 상단 가로 탭바로 마일리지·친구초대 접근 제공
  if (isMobile) {
    return isMyPage && !!user ? (
      <MobileWrapper>
        <MobileTabs>
          {MY_PAGE_TABS.map((tab) => (
            <MobileTab
              key={tab.id}
              type='button'
              isSelected={tab.id === activeTabId}
              onClick={() => handleTabClick(tab.pathname)}
            >
              {tab.label}
            </MobileTab>
          ))}
        </MobileTabs>
        {children}
      </MobileWrapper>
    ) : (
      children
    );
  }

  return isMyPage && !!user ? (
    <div className='flex w-[1000px] justify-between'>
      <SideBarWrapper>
        <SideBarTitle>마이페이지</SideBarTitle>
        <MenuWrapper>
          {MY_PAGE_TABS.map((tab) => (
            <MenuTab
              key={tab.id}
              isSelected={tab.id === activeTabId}
              onClick={() => handleTabClick(tab.pathname)}
            >
              {tab.label}
            </MenuTab>
          ))}
        </MenuWrapper>
      </SideBarWrapper>
      {children}
    </div>
  ) : (
    children
  );
};

export default MyPageWrapper;

const MobileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MobileTabs = styled.div`
  display: flex;
  gap: 20px;
  padding: 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const MobileTab = styled.button<{ isSelected?: boolean }>`
  flex: none;
  padding: 15px 2px 13px;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  ${({ theme }) => theme.FONT.B1};
  color: ${({ isSelected, theme }) =>
    isSelected ? getPrimaryColor(app) : theme.COLOR.GREY_02};
  font-weight: ${({ isSelected }) => (isSelected ? 800 : 600)};
  border-bottom: 2px solid
    ${({ isSelected }) => (isSelected ? getPrimaryColor(app) : 'transparent')};
  margin-bottom: -1px;
`;

const SideBarWrapper = styled.div`
  position: sticky;
  top: 144px;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 140px;
  height: fit-content;
  gap: 60px;
`;

const SideBarTitle = styled.p`
  ${({ theme }) => theme.NEW_FONT.H1};
`;

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

const MenuTab = styled.p<{ isSelected?: boolean }>`
  ${({ theme }) => theme.NEW_FONT.H5};
  color: ${({ isSelected, theme }) =>
    isSelected ? getPrimaryColor(app) : theme.COLOR.BLACK};
  cursor: pointer;
`;
